import {
  AutoPlanStatus,
  CommitmentPlanSortBy,
  CommitmentPlanSortDirection,
  InvestorDetailsPacingTabCommitmentPlansQuery,
  PaginateSortDirection
} from '@/__generated__/graphql';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import Button from '@/components/basicComponents/button';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { IItems } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { INVESTOR_LEGAL_ENTITIES_QUERY } from '../../../queries';
import { InvestorDetailsEntityTypeEnum } from '../../../types';
import AutoPlanWizardModalPage from '../modalPages/autoPlanWizardModalPage';
import { COMMITMENT_PLANS_QUERY, UPDATE_COMMITMENT_PLAN_MUTATION } from '../queries';
import Table from './allCommitmentPlansTable/table';
import { mobileTableSort } from './constants';
import DeletePlan from './modals/deletePlan';

export const AllCommitmentPlans = () => {
  const params = useParams();
  const { isMobile } = useResponsive();
  const goBack = useGoBack();
  const navigate = useNavigate();

  const { searchParams, onChangeSearch, onChangePaginationData, onChangeSort } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const key = (searchParams.get('key') || CommitmentPlanSortBy.StartDate) as CommitmentPlanSortBy;
  const asc = searchParams.get('asc') === 'true' || false;
  const backToTitle = searchParams.get('backToTitle') || '';
  const entityName = searchParams.get('entityName') || '';
  const q = searchParams.get('q') || '';
  const isGroup = searchParams.get('isGroup') === 'true';

  const [entityType, setEntityType] = useState<InvestorDetailsEntityTypeEnum>(null);
  const [tableRows, setTableRows] = useState<InvestorDetailsPacingTabCommitmentPlansQuery['commitmentPlans']['data']>([]);
  const [currentRow, setCurrentRow] = useState<InvestorDetailsPacingTabCommitmentPlansQuery['commitmentPlans']['data'][number] | null>(null);
  const [modalWindow, setModalWindow] = useState({
    isOpen: false,
    type: 'auto-plan'
  });
  const [isAutoPlanWizardPageOpen, setIsAutoPlanWizardPageOpen] = useState(false);
  const [mobileSort, setMobileSort] = useState(mobileTableSort[0].title);
  const sortItem = mobileTableSort.find((item) => item.title === mobileSort);
  const mobileSortList = mobileTableSort.map((item) => item.title);

  const [updateCommitmentPlan, { loading: updateCommitmentPlanLoading }] = useMutation(UPDATE_COMMITMENT_PLAN_MUTATION, {
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: investorLegalEntitiesLoading } = useQuery(INVESTOR_LEGAL_ENTITIES_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.investorId,
    variables: {
      data: {
        id: params?.investorId,
        legalEntitiesList: {
          sortBy: 'name',
          sortDirection: PaginateSortDirection.Asc
        }
      }
    },
    onCompleted: (data) => {
      const { legalEntities } = data.getInvestor;

      const topLevelEntity = legalEntities.find((entity) => entity.id === entityId);
      if (topLevelEntity?.isGroup && isGroup) {
        setEntityType(InvestorDetailsEntityTypeEnum.Group);
        return;
      }

      const groupEntity = legalEntities.find((entity) => entity.entities?.some((groupedEntity) => groupedEntity.id === entityId));

      if (groupEntity) {
        setEntityType(InvestorDetailsEntityTypeEnum.GroupedEntity);
        return;
      }

      if (topLevelEntity) {
        setEntityType(InvestorDetailsEntityTypeEnum.UngroupedEntity);
        return;
      }

      setEntityType(InvestorDetailsEntityTypeEnum.Household);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const {
    data: commitmentPlansData,
    refetch: commitmentPlansRefetch,
    networkStatus: commitmentPlansNetworkStatus
  } = useQuery(COMMITMENT_PLANS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        legalEntityId: entityId,
        search: q.toLowerCase().trim(),
        sortBy: key,
        sortDirection: asc ? CommitmentPlanSortDirection.Asc : CommitmentPlanSortDirection.Desc,
        limit: Number(limit),
        offset: Number(offset)
      }
    },
    onCompleted: (data) => {
      setTableRows(data.commitmentPlans.data);
    }
  });

  useEffect(() => {
    if (!isMobile) return;
    commitmentPlansRefetch({
      input: {
        legalEntityId: entityId,
        search: q.toLowerCase().trim(),
        sortBy: sortItem?.key as CommitmentPlanSortBy,
        sortDirection: sortItem?.asc ? CommitmentPlanSortDirection.Asc : CommitmentPlanSortDirection.Desc,
        limit: Number(limit),
        offset: Number(offset)
      }
    });
  }, [mobileSort]);

  const handleAutoPlanWizardPage = ({ open }: { open: boolean }) => {
    setIsAutoPlanWizardPageOpen(open);
  };

  const openModalWindow = (type: string) => {
    setModalWindow({ type, isOpen: true });
  };

  const closeModalWindow = () => {
    setCurrentRow(null);
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const deleteCommitmentPlan = (row: InvestorDetailsPacingTabCommitmentPlansQuery['commitmentPlans']['data'][number]) => {
    setCurrentRow(row);
    openModalWindow('delete-plan');
  };

  const makeActivePlan = (row: InvestorDetailsPacingTabCommitmentPlansQuery['commitmentPlans']['data'][number]) => {
    setCurrentRow(row);
    updateCommitmentPlan({
      variables: {
        input: {
          id: row.id,
          active: true
        }
      },
      onCompleted: () => {
        commitmentPlansRefetch();
      }
    });
  };

  const dropdownActions = (row: InvestorDetailsPacingTabCommitmentPlansQuery['commitmentPlans']['data'][number]): IItems[] => {
    const isCommitmentPlanComplete =
      row.autoPlanStatus !== AutoPlanStatus.InProgress &&
      row.autoPlanStatus !== AutoPlanStatus.WaitingForReview &&
      row.autoPlanStatus !== AutoPlanStatus.Failed;

    return [
      ...(isCommitmentPlanComplete
        ? [
            {
              name: 'Edit Plan',
              onClick: () =>
                navigate(
                  `/investors/${investorId}/${entityId}/editCommitmentPlan?commitmentPlanId=${row.id}&isGroup=${isGroup}&backToTitle=All Commitment Plans`
                )
            }
          ]
        : [
            {
              name: 'Edit Plan',
              onClick: () =>
                navigate(
                  `/investors/${investorId}/${entityId}?activeTab=Pacing&commitmentPlanId=${row.id}&isGroup=${isGroup}&backToTitle=All Commitment Plans`
                )
            }
          ]),
      {
        name: 'View Forecast',
        onClick: () =>
          navigate(
            `/investors/${investorId}/${entityId}?activeTab=Pacing&commitmentPlanId=${row.id}&isGroup=${isGroup}&backToTitle=All Commitment Plans`
          )
      },
      ...(isCommitmentPlanComplete
        ? [
            {
              name: 'Make Active Plan',
              onClick: () => makeActivePlan(row)
            }
          ]
        : []),
      ...(isCommitmentPlanComplete
        ? [
            {
              name: 'Duplicate Plan',
              onClick: () =>
                navigate(
                  `/investors/${investorId}/${entityId}/editCommitmentPlan?commitmentPlanId=${row.id}&duplicate=true&isGroup=${isGroup}&backToTitle=All Commitment Plans`
                )
            }
          ]
        : []),
      {
        name: 'Delete Plan',
        onClick: () => deleteCommitmentPlan(row)
      }
    ];
  };

  if (investorLegalEntitiesLoading) {
    return <LoaderOnWholeScreen />;
  }

  if (isAutoPlanWizardPageOpen) {
    return <AutoPlanWizardModalPage entityType={entityType} backToTitle="All Commitment Plans" handleAutoPlanWizardPage={handleAutoPlanWizardPage} />;
  }

  return (
    <>
      {/* {modalWindow.isOpen && modalWindow.type === 'auto-plan' && (
        <AutoPlan isOpen={modalWindow.isOpen} onClose={closeModalWindow} entityName={entityName} openAutoPlanDialogFrom="allCommitmentPlans" />
      )} */}
      {modalWindow.isOpen && modalWindow.type === 'delete-plan' && (
        <DeletePlan isOpen={modalWindow.isOpen} onClose={closeModalWindow} currentRow={currentRow} refetch={commitmentPlansRefetch} />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/investors' })} backToTitle={backToTitle} />} />
        <PageTitle title="All Commitment Plans" />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <AddButtonWrapper>
            <Button size="md" onClick={() => handleAutoPlanWizardPage({ open: true })} icon={<AddButtonIcon />}>
              Add New Commitment Plan
            </Button>
          </AddButtonWrapper>
          <FilterPage
            search={{
              value: q,
              onChange: onChangeSearch,
              placeholder: 'Search'
            }}
            refetch={commitmentPlansRefetch}
            resultsValue={commitmentPlansData?.commitmentPlans.total ?? 0}
            mobileSort={{
              value: mobileSort,
              onChange: setMobileSort,
              selects: mobileSortList
            }}
          />
          <Table
            tableRows={tableRows}
            loading={commitmentPlansNetworkStatus === NetworkStatus.loading || commitmentPlansNetworkStatus === NetworkStatus.setVariables}
            sort={{ key, asc }}
            updateLoading={updateCommitmentPlanLoading || commitmentPlansNetworkStatus === NetworkStatus.refetch}
            currentRow={currentRow}
            setSort={onChangeSort}
            dropdownActions={dropdownActions}
          />
          <PaginationWrap>
            <TablePagination
              savePagination={onChangePaginationData}
              paginationValues={{
                limit: Number(limit),
                offset: Number(offset),
                total: commitmentPlansData?.commitmentPlans.total ?? 0
              }}
              refetch={() => null as any}
            />
          </PaginationWrap>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const AddButtonWrapper = styled.div`
  margin-top: 50px;
`;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
